import * as React from 'react'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import {
  isMainBranch,
  useCreateBranchMutation,
  useSwitchBranch,
  type Branch
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { newOperationKey } from './newOperationKey'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../ui/dialog'

export interface BranchCreateDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Branch to fork from; undefined or a protected branch forks from main. */
  source?: Branch
}

/**
 * Name-and-create dialog for a new branch forked from the given source (the
 * caller's current branch). Creation is an O(1) pointer insert off the
 * source's head commit; on success the new branch is opened and made active.
 */
export function BranchCreateDialog({ open, onOpenChange, source }: BranchCreateDialogProps) {
  const [createBranch, createState] = useCreateBranchMutation()
  const { switchBranch } = useSwitchBranch()
  const [name, setName] = React.useState('')
  const [submitError, setSubmitError] = React.useState('')
  const createKey = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!open) {
      setName('')
      setSubmitError('')
      createKey.current = null
      createState.reset()
    }
  }, [open, createState])

  const trimmed = name.trim()
  const submitting = createState.isLoading
  const canSubmit = trimmed.length > 0 && !submitting
  // Forking from a user-protected branch is fine (a fork is a read of its
  // head); only main itself uses the omitted-source form.
  const forkFromMain = !source || isMainBranch(source)
  const sourceName = forkFromMain ? 'main' : source.name

  React.useEffect(() => {
    createKey.current = null
  }, [trimmed, source?.id])

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!canSubmit) return
    setSubmitError('')
    createKey.current ??= newOperationKey()
    try {
      const created = await createBranch({
        name: trimmed,
        idempotencyKey: createKey.current,
        ...(forkFromMain ? {} : { sourceId: source.id })
      }).unwrap()
      toast.success(`Created ${created.name}`)
      onOpenChange(false)
      void switchBranch(created.id).catch(() => {
        toast.error(`Could not open ${created.name}`)
      })
    } catch (err) {
      setSubmitError(extractCreateError(err))
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (submitting) return
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Create branch</DialogTitle>
          <DialogDescription>
            Forks from <span className="font-mono text-ink">{sourceName}</span> at its current head.
            The new branch becomes active once it opens.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <Input
            value={name}
            onChange={(event) => {
              setName(event.target.value)
              setSubmitError('')
            }}
            placeholder="branch-name"
            aria-label="New branch name"
            autoComplete="off"
            autoFocus
            className="font-mono"
            disabled={submitting}
          />

          {submitError ? (
            <p role="alert" className="text-sm text-st-down">
              {submitError}
            </p>
          ) : null}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
              Create branch
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function extractCreateError(err: unknown): string {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = (err as { data?: unknown }).data
    if (data && typeof data === 'object' && 'message' in data) {
      const message = (data as { message?: unknown }).message
      if (typeof message === 'string' && message.length > 0) return message
    }
  }
  return 'Could not create branch.'
}
